import ContextPadProvider from 'bpmn-js/lib/features/context-pad/ContextPadProvider';
import EventBus from 'diagram-js/lib/core/EventBus';
import ContextPad from 'diagram-js/lib/features/context-pad/ContextPad';
import {ActionItems, Base} from 'diagram-js/lib/model';
import {isArray, set} from 'lodash';
import {END_NODE_ID, subProcessType, taskType} from '../../../../../../ispm-web/shared/modules/bpmn/index';
import {PathwaysModeling} from '@ispm-client/modules/bpmn-modeler/bpmn/commands/pathways-modeling';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {PathwaysAuthoringState} from '@ispm/shared/constants/pathways-authoring-state';
import {select, Store} from "@ngrx/store";
import {State} from "@ispm-client/modules/bpmn-modeler/store/state";
import {take} from "rxjs/operators";
import {selectClipboard} from "@ispm-client/modules/bpmn-modeler/store/selectors/clipboard-data.selector";
import {TreeNavigator} from "@ispm/shared/bpmn-io/services/tree-navigator";
import {EventType} from "@ispm/shared/bpmn-io/bpmn-js";

export class PathwaysContextPadProvider extends ContextPadProvider {

  constructor(private config, private injector, private eventBus: EventBus,
              private contextPad: ContextPad, private modeling, private elementFactory,
              private connect, private create, private popupMenu,
              private canvas, private rules, private translate, private pathwaysModeling: PathwaysModeling,
              private stateConfig: PathwaysAuthoringState, private store$: Store<State>, private treeNavigator: TreeNavigator) {
    super(config, injector, eventBus, contextPad, modeling, elementFactory, connect, create, popupMenu, canvas,
      rules, translate);
    // No need to register, base class already did
  }

  getContextPadEntries(element: Base): ActionItems {
    const actions: ActionItems = {};
    const nodeType = ModelingUtil.getNodeType(element);
    if (ModelingUtil.isFloatedGateway(element)) {
      this.appendDeleteAction(element, actions);
    } else {
      if (!ModelingUtil.hasDeletionTrackingChange(element)) {
        if (nodeType !== undefined) {
          switch (nodeType) {
            case NodeType.normal:
              this.appendAddAction(element, actions);
              this.appendAddTreatmentAction(element, actions);
              this.appendConnectAction(element, actions);
              this.appendNeighborhoodAction(element, actions);
              this.appendPasteAction(element, actions);
              this.appendCopyAction(actions);
              this.appendCopyHierarchyAction(element, actions);
              this.appendDeleteAction(element, actions);
              break;
            case NodeType.neighborhood_entry:
              this.appendCopyAction(actions);
              this.appendCopyHierarchyAction(element, actions);
              this.appendDeleteAction(element, actions);
              break;
            case NodeType.neighborhood_head:
              this.appendAddAction(element, actions);
              this.appendAddTreatmentAction(element, actions);
              this.appendPasteAction(element, actions);
              this.appendCopyHierarchyAction(element, actions);
              this.appendConnectAction(element, actions);
              break;
            case NodeType.treatment:
              this.appendCopyAction(actions);
              this.appendDeleteAction(element, actions);
              break;
            case NodeType.head:
              this.appendAddAction(element, actions);
              this.appendAddTreatmentAction(element, actions);
              this.appendNeighborhoodAction(element, actions);
              this.appendConnectAction(element, actions);
              this.appendPasteAction(element, actions);
              this.appendCopyHierarchyAction(element, actions);
              break;
          }
        } else {
          // element is gateway or connection
          if (ModelingUtil.isConnection(element)) {
            this.appendDeleteAction(element, actions);
          }
        }
      } else {
        if (ModelingUtil.isFloatedConnection(element)) {
          this.appendDeleteAction(element, actions);
        }
      }
    }

    return actions;
  }

  appendNewNode(event, element) {
    this.pathwaysModeling.appendNewNode(element, subProcessType, NodeType.normal);

  }

  appendTreatmentNode(event, element) {
    this.pathwaysModeling.appendNewNode(element, taskType, NodeType.treatment);
  }

  appendNeighborhoodEntryNode(event, element) {
    this.pathwaysModeling.appendNewNode(element, subProcessType, NodeType.neighborhood_entry);
  }

  startConnect(event, element) {
    this.connect.start(event, element);
  }

  pasteElement(event, element) {
    this.pathwaysModeling.pasteNode(element);
  }

  copyElement(event, element) {
    this.pathwaysModeling.copyNode(element);
  }

  copyElemenHierarchy(event, element) {
    this.pathwaysModeling.copyNodeHierarchy(element);
  }

  //delete node click handler
  private clickDelete(event: any, element: Base) {
    this.eventBus.fire(EventType.OPEN_DELETE_WARNING, {element: element});
  }


  private appendAddAction(element: Base, actions: ActionItems) {
    Object.assign(actions,
      {
        'newNode': {
          group: 'node',
          title: 'Add a new node',
        }
      });
    if (!ModelingUtil.hasChildOfType(element, NodeType.treatment) || this.allElementConnectionsToTreatmentNodesWereDeleted(element) || ModelingUtil.hasOutgoingConnectionWithDeletedTrackingChange(element)) {
      set(actions, 'newNode.className', 'pathways-context-pad-icon icon__Plus');
      set(actions, 'newNode.action', this.appendNewNode.bind(this));
    } else {
      set(actions, 'newNode.className', 'pathways-context-pad-icon disabled icon__Plus');
    }
  }

  private isAllTreatmentChildsDeleted(element: Base) {
    return ModelingUtil.isAllChildsOfGivenNodeTypeHasTheSameTR(element, NodeType.treatment, 'deleted');
  }

  private allElementConnectionsToTreatmentNodesWereDeleted(element: Base) {
    return ModelingUtil.allOutGoingConnectionsToChildOfTypeHasSameTrackingChange(element, NodeType.treatment, 'deleted');
  }

  private appendAddTreatmentAction(element: Base, actions: ActionItems) {
    Object.assign(actions,
      {
        'treatmentNewNode': {
          group: 'treatment',
          title: 'Add a treatment node'
        }
      });

    if (!ModelingUtil.hasOutgoingGateway(element) && (!ModelingUtil.hasActiveChild(element) || ModelingUtil.AllOutgoingConnectionWithDeletedTrackingChange(element))) {
      set(actions, 'treatmentNewNode.className', 'pathways-context-pad-icon icon__dls-pills-white');
      set(actions, 'treatmentNewNode.action', this.appendTreatmentNode.bind(this));
    } else {
      set(actions, 'treatmentNewNode.className', 'pathways-context-pad-icon disabled icon__dls-pills-white');
    }
  }

  private appendDeleteAction(element: Base, actions: ActionItems) {
    // delete element entry, only show if allowed by rules
    const isConnection = ModelingUtil.isConnection(element);
    if (isConnection && ModelingUtil.getConnectionTarget(element).id === END_NODE_ID) {
      return;
    }
    let deleteAllowed = this.rules.allowed('elements.delete', {elements: [element]});
    if (isArray(deleteAllowed)) {
      // was the element returned as a deletion candidate?
      deleteAllowed = deleteAllowed[0] === element;
    }
    const clickFunction = this.clickDelete.bind(this);
    if (deleteAllowed) {
      Object.assign(actions, {
        'delete': {
          group: 'edit',
          className: 'pathways-context-pad-icon icon__DeleteTrash',
          title: this.translate('Remove'),
          action: {
            click: clickFunction //when clicking on delete, fire an event to modeler to open popup
          }
        }
      });
    }
  }

  private appendConnectAction(element: Base, actions: ActionItems) {
    const startConnect = this.startConnect.bind(this);

    Object.assign(actions, {
      'connect': {
        group: 'connect',
        title: this.translate('Connect using Sequence Flow'),
      }
    });

    if (ModelingUtil.hasChildOfType(element, NodeType.treatment) && !(this.allElementConnectionsToTreatmentNodesWereDeleted(element))) {
      set(actions, 'connect.className', 'pathways-context-pad-icon disabled icon__dls-add-new-copy');
      set(actions, 'connect.action', {click: undefined, dragstart: undefined});
    } else {
      set(actions, 'connect.className', 'pathways-context-pad-icon icon__dls-add-new-copy');
      set(actions, 'connect.action', {click: startConnect, dragstart: startConnect});
    }
  }

  private appendNeighborhoodAction(element: Base, actions: ActionItems) {
    const listOfAncestors: Base[] = [].concat.apply([], ModelingUtil.getNodeAncestors(element));
    const hasNeighborhoodHead = listOfAncestors.some(el => ModelingUtil.getNodeType(el) === NodeType.neighborhood_head);
    const hasTreatmentNode = ModelingUtil.hasChildOfType(element, NodeType.treatment)
      && !this.isAllTreatmentChildsDeleted(element)
      && !ModelingUtil.hasOutgoingConnectionWithDeletedTrackingChange(element);

    const appendNeighborhoodHeadNode = this.appendNeighborhoodEntryNode.bind(this);
    Object.assign(actions, {
      'mark': {
        group: 'neighborhood',
        title: this.translate('Add Neighborhood node'),
      }
    });

    if (hasNeighborhoodHead || hasTreatmentNode || this.treeNavigator.navigatedToNeighborhood()) {
      set(actions, 'mark.className', 'pathways-context-pad-icon disabled icon__dls-add-new');
      set(actions, 'mark.action', {click: undefined});
    } else {
      set(actions, 'mark.className', 'pathways-context-pad-icon icon__dls-add-new');
      set(actions, 'mark.action', {click: appendNeighborhoodHeadNode});
    }

  }

  private appendCopyAction(actions: ActionItems) {

    Object.assign(actions, {
      'copy': {
        group: 'edit',
        title: this.translate('Copy'),
      }
    });
    set(actions, 'copy.className', 'pathways-context-pad-icon icon__Copy');
    set(actions, 'copy.action', {click: this.copyElement.bind(this)});
  }

  private appendCopyHierarchyAction(element: Base, actions: ActionItems) {

    Object.assign(actions, {
      'hierarchy': {
        group: 'edit',
        title: this.translate('Copy Hierarchy'),
      }
    });
    if (ModelingUtil.hasChildren(element)) {
      set(actions, 'hierarchy.className', 'pathways-context-pad-icon icon__DLS_Copy-Hierarchy-V2');
      set(actions, 'hierarchy.action', {click: this.copyElemenHierarchy.bind(this)});

    } else {
      set(actions, 'hierarchy.className', 'pathways-context-pad-icon disabled icon__DLS_Copy-Hierarchy-V2');
      set(actions, 'hierarchy.action', {click: undefined});
    }
  }

  private appendPasteAction(element: Base, actions: ActionItems) {

    this.store$.pipe(select(selectClipboard), take(1))
      .subscribe(clipboard => {
        let pasteAllowed = false;
        // there is something in clipboard ?
        if (clipboard && clipboard.copiedObject) {
          const storedNode = clipboard.copiedObject;
          const isContainNeighborhood = clipboard.isContainNeighborhood;

          const storedDataType = storedNode['type'];
          const onMainPage = this.treeNavigator.currentLayerIsMainTree();
          const elementType = ModelingUtil.getNodeType(element);

          if (!onMainPage && isContainNeighborhood) {
            // prevent pasting neighborhood inside neighborhood
            pasteAllowed = false
          } else {
            // on head node: paste only regular nodes
            if (elementType === NodeType.head) {
              pasteAllowed = storedDataType === NodeType.normal || storedDataType == NodeType.neighborhood_entry;
            } else {
              if (elementType !== NodeType.neighborhood_entry &&
                (ModelingUtil.AllChildsWithDeletedTrackingChange(element) || !ModelingUtil.hasChildren(element))) {
                pasteAllowed = true;
              } else {
                const allTreatmentChildrenAreDeleted = (ModelingUtil.hasChildOfType(element, NodeType.treatment) && this.allElementConnectionsToTreatmentNodesWereDeleted(element));
                const hasNoTreatmentChild = !ModelingUtil.hasChildOfType(element, NodeType.treatment) || allTreatmentChildrenAreDeleted;
                // on Main page
                if (onMainPage) {
                  switch (elementType) {
                    case NodeType.normal:
                      if ((storedDataType == NodeType.normal && hasNoTreatmentChild)
                        || (storedDataType == NodeType.treatment && !ModelingUtil.hasChildren(element))
                        || (storedDataType == NodeType.neighborhood_entry && hasNoTreatmentChild))
                        pasteAllowed = true;
                      break;
                  }
                }
                // on subtree page
                else {
                  if (storedDataType != NodeType.neighborhood_entry) {
                    switch (elementType) {
                      case NodeType.normal:
                        if ((storedDataType == NodeType.normal && hasNoTreatmentChild)
                          || (storedDataType == NodeType.treatment && !ModelingUtil.hasChildren(element)))
                          pasteAllowed = true;
                        break;
                      case NodeType.neighborhood_entry:
                      case NodeType.neighborhood_head:
                        if ((storedDataType == NodeType.normal)
                          || (storedDataType == NodeType.treatment && !ModelingUtil.hasChildren(element)))
                          pasteAllowed = true;
                        break;
                    }
                  }
                }
              }
            }
          }
        }

        Object.assign(actions, {
          'paste': {
            group: 'edit',
            title: this.translate('Paste')
          }
        });
        if (pasteAllowed) {
          set(actions, 'paste.className', 'pathways-context-pad-icon icon__Paste');
          set(actions, 'paste.action', {click: this.pasteElement.bind(this)});
        } else {
          set(actions, 'paste.className', 'pathways-context-pad-icon disabled icon__Paste');
          set(actions, 'paste.action', {click: undefined});
        }
      }).unsubscribe();

  }

}

PathwaysContextPadProvider.$inject = [...ContextPadProvider.$inject, 'pathwaysModeling', 'config.state', 'store$', 'treeNavigator'];
